import { NgIf, NgFor, AsyncPipe, DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { ExtendedNotificationComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  CarbonFootprintResponse,
  CarbonFootprintResultFound,
  Impact,
  ProcessingStatus,
} from 'src/app/cycle/models/cycle.model';
import { Cycle } from 'src/app/cycle/models/flock-registration-service.model';
import {
  getCarbonFootprint,
  getCycles,
  selectSustainabilityNoticeVisibility,
} from 'src/app/cycle/store/selectors/cycle.selectors';

import {
  CarbonFootprintReadiness,
  CarbonFootprintIssue,
} from '../../../models/carbon-footprint-readiness.model';
import { FlockRegistrationService } from '../../../services/flock-registration.service';

import * as cycleActions from '../../../store/actions/cycle.actions';

@Component({
  selector: 'app-carbon-footprint',
  templateUrl: './carbon-footprint.component.html',
  styleUrls: ['./carbon-footprint.component.scss'],
  imports: [
    ExtendedNotificationComponent,
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatButton,
    MatIcon,
    MatProgressBar,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
    RouterLink,
    DatePipe,
    DecimalPipe,
    NgTemplateOutlet,
  ],
})
export class CarbonFootprintComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly dialogService = inject(DialogService);
  private readonly flockRegistrationService = inject(FlockRegistrationService);
  private readonly actions = inject(Actions);

  private readonly destroyed$ = new Subject<void>();
  private subscription: Subscription;

  public showNotification = true;

  cycles$: Observable<Cycle[]> = this.store.select(getCycles);
  readonly currentLocale$ = this.locale.locale$;
  carbonFootprint$: Observable<CarbonFootprintResponse> = this.store.select(getCarbonFootprint);
  selectedCycle: Cycle = null;

  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource<Impact>([]),
    columns: [],
  };
  carbonFootprintErrors: string[] = [];
  readonly calculations = signal<CarbonFootprintResultFound[]>([]);
  readonly selectedCalculationId = signal<number | null>(null);
  readonly selectedCalculation = computed(() =>
    this.calculations().find((result) => result.id === this.selectedCalculationId()),
  );
  readonly loadingResults = signal(false);
  readonly resultsError = signal(false);
  readonly calculating = signal(false);
  readonly calculationFailed = signal(false);
  readonly readiness = signal<CarbonFootprintReadiness | null>(null);
  readonly checkingReadiness = signal(false);
  readonly readinessError = signal(false);
  readonly readinessChecks = computed(() =>
    [...(this.readiness()?.checks ?? [])].sort((a, b) => Number(a.complete) - Number(b.complete)),
  );
  readonly completedRequirements = computed(
    () => this.readinessChecks().filter((check) => check.complete).length,
  );
  readonly canCalculate = computed(
    () =>
      !this.checkingReadiness() &&
      !this.calculating() &&
      !this.readinessError() &&
      this.readiness()?.canCalculate === true &&
      ['cycle', 'farm-resources', 'bedding', 'feeds'].every((requirement) =>
        this.readiness()?.checks.some(
          (check) => check.requirement === requirement && check.complete,
        ),
      ),
  );
  private readonly readinessRequests = new Subject<number | null>();
  sustellLogoSrc = 'assets/images/sustell_logo.png';

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    this.store.dispatch(cycleActions.loadCyclesRequest());
    this.readinessRequests
      .pipe(
        switchMap((cycleId) => {
          this.readiness.set(null);
          this.readinessError.set(false);
          this.checkingReadiness.set(cycleId !== null);
          if (cycleId === null) return EMPTY;
          return this.flockRegistrationService.getCarbonFootprintReadiness(cycleId).pipe(
            tap((readiness) => {
              this.readiness.set(readiness);
              this.checkingReadiness.set(false);
            }),
            catchError(() => {
              this.readinessError.set(true);
              this.checkingReadiness.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.actions
      .pipe(ofType(cycleActions.calculateCarbonFootprintFailure), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.calculating.set(false);
        this.calculationFailed.set(true);
        this.checkReadiness();
      });
    this.actions
      .pipe(ofType(cycleActions.loadCarbonFootprintRequest), takeUntil(this.destroyed$))
      .subscribe(({ cycleId }) => {
        if (cycleId !== this.selectedCycle?.id) return;
        this.loadingResults.set(true);
        this.resultsError.set(false);
      });
    this.actions
      .pipe(ofType(cycleActions.loadCarbonFootprintError), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loadingResults.set(false);
        this.calculating.set(false);
        this.resultsError.set(true);
      });

    this.tableConfig = {
      ...this.tableConfig,
      pagination: false,
      selectColumn: false,
      actionColumn: false,
      scrolling: true,
      disableCards: true,
      wrapperWidth: '100%',
      columns: [
        {
          id: 'name',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('sustainability.calculations.source'),
          disableFormatting: true,
        },
        {
          id: 'value',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.calculations.value'),
          digitsInfo: '1.03-03',
        },
      ],
      actions: [],
    };

    this.carbonFootprint$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: CarbonFootprintResponse) => {
        if (!this.selectedCycle) return;
        const results = (response?.results ?? []).filter(
          (result) => result.cycleId === this.selectedCycle.id,
        );
        // Ignore results from a request for another cycle.
        if (response?.results?.length && !results.length) return;
        const previousIds = new Set(this.calculations().map((result) => result.id));
        const newSuccess = results.find(
          (result) => !previousIds.has(result.id) && this.resultStatus(result) === 'complete',
        );
        const previousSelection = results.find(
          (result) => result.id === this.selectedCalculationId(),
        );
        const selected = this.calculating()
          ? results[0]
          : (newSuccess ?? previousSelection ?? results[0]);
        this.calculations.set(results);
        this.loadingResults.set(false);
        this.calculating.set(false);
        this.calculationFailed.set(
          results.length > 0 && this.resultStatus(results[0]) === 'failed',
        );
        this.selectCalculation(selected?.id ?? null);
      });

    this.cycles$.pipe(takeUntil(this.destroyed$)).subscribe((cycles) => {
      const selected = cycles.find((cycle) => cycle.id === this.selectedCycle?.id);
      if (selected) {
        this.selectedCycle = selected;
      } else if (cycles.length) {
        this.onCycleSelected(cycles[0]);
      } else {
        this.selectedCycle = null;
        this.readinessRequests.next(null);
        this.clearResults();
        this.store.dispatch(cycleActions.resetCarbonFootprint());
      }
    });

    this.subscription = this.store
      .select(selectSustainabilityNoticeVisibility)
      .subscribe((visible) => (this.showNotification = visible));
  }

  onCycleSelected(cycle: Cycle): void {
    this.selectedCycle = cycle;
    this.clearResults();
    this.checkReadiness();

    this.store.dispatch(cycleActions.setSelectedCycle({ cycleId: cycle.id }));
    this.loadResults();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public getActionText() {
    return this.showNotification
      ? this.locale.translate('sustainability.action-text-close')
      : this.locale.translate('sustainability.action-text-open');
  }

  public toggleNotification() {
    this.store.dispatch(
      cycleActions.setSustainabilityNoticeVisibility({ visible: !this.showNotification }),
    );
  }

  startCalculateCarbonFootprint(): void {
    if (!this.selectedCycle || !this.canCalculate()) return;
    const cycleId = this.selectedCycle.id;

    this.dialogService
      .confirmWarning({
        title: this.locale.translate('sustainability.carbon-footprint-title'),
        text: this.locale.translate('sustainability.carbon-footprint-text'),
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (
          result.status === DIALOG_RETURN_TYPES.confirm &&
          this.selectedCycle?.id === cycleId &&
          this.canCalculate()
        ) {
          this.calculating.set(true);
          this.calculationFailed.set(false);
          this.store.dispatch(cycleActions.calculateCarbonFootprintRequest({ cycleId }));
        }
      });
  }

  refreshCarbonFootprintResult(): void {
    if (!this.selectedCycle || this.calculating() || this.loadingResults()) return;
    this.store.dispatch(
      cycleActions.refreshCarbonFootprintRequest({ cycleId: this.selectedCycle.id }),
    );
  }

  checkReadiness(): void {
    if (this.selectedCycle) this.readinessRequests.next(this.selectedCycle.id);
  }

  loadResults(): void {
    if (!this.selectedCycle) return;
    this.loadingResults.set(true);
    this.resultsError.set(false);
    this.store.dispatch(
      cycleActions.loadCarbonFootprintRequest({ cycleId: this.selectedCycle.id }),
    );
  }

  selectCalculation(id: number | null): void {
    this.selectedCalculationId.set(id);
    const result = this.selectedCalculation();
    const footprint = result?.carbonFootprintResult?.footprint;
    const total = result ? this.climateImpact(result) : undefined;
    this.carbonFootprintErrors = (footprint?.errors ?? [])
      .filter((error) => error.key === 'feed-import.externalMsg')
      .flatMap((error) => error.messages ?? [])
      .filter((message) => message.trim().length > 0);
    this.tableConfig = {
      ...this.tableConfig,
      dataSource: new MatTableDataSource<Impact>(
        (footprint?.impacts ?? [])
          .filter((impact) => impact !== total)
          .map((impact) => ({
            ...impact,
            name: impact.name?.replaceAll('_', ' ').replace(/^climate change\s*-\s*/i, ''),
          })),
      ),
      columns: this.tableConfig.columns.map((column) =>
        column.id === 'value'
          ? {
              ...column,
              label:
                footprint?.impacts?.[0]?.unit ??
                this.locale.translate('sustainability.calculations.value'),
            }
          : column,
      ),
    };
  }

  resultStatus(result: CarbonFootprintResultFound): 'complete' | 'pending' | 'failed' {
    const calculation = result.carbonFootprintResult;
    if (calculation?.footprint?.errors?.length) return 'failed';
    if (
      calculation?.status === ProcessingStatus.PENDING ||
      !calculation?.footprint?.impacts?.length
    )
      return 'pending';
    return 'complete';
  }

  climateImpact(result: CarbonFootprintResultFound): Impact | undefined {
    return result.carbonFootprintResult?.footprint?.impacts?.find(
      (impact) => impact.name?.replaceAll('_', ' ').toLowerCase() === 'climate change',
    );
  }

  private clearResults(): void {
    this.calculations.set([]);
    this.selectCalculation(null);
    this.calculationFailed.set(false);
    this.resultsError.set(false);
    this.loadingResults.set(false);
    this.calculating.set(false);
  }

  issueMessage(issue: CarbonFootprintIssue): string {
    const message = this.locale.translate('sustainability.readiness.issues.' + issue.code);
    return issue.year ? message + ' ' + issue.year : message;
  }

  fieldLabel(field: string): string {
    return this.locale.translate('sustainability.readiness.fields.' + field);
  }
}
